import PropTypes from 'prop-types'
import { useState, useEffect, useCallback, useRef, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { ESTADO_PUBLICADA } from '../../constants/publicaciones.js'
import { aplicarOrdenamiento } from '../../helpers/aplicarOrdenamiento'
import { getDeepestCommonCategoryId } from '../../helpers/getDeepestCommonCategoryId.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import {
  setCategoriaPath,
  setEstado,
  setTipoVendedor,
  setPublicacionesFiltradas,
  setOrdenamiento
} from '../../slices/filterSlice.js'
import { CarritoContext } from '../context/carritoContext'
import { Filtros } from '../reutilizables/filtros/Filtros'
import { OrdenarPor } from '../reutilizables/ordenamiento/OrdenarPor.jsx'
import { VistaCuadricula } from '../reutilizables/ordenamiento/VistaCuadricula.jsx'
import { Paginacion } from '../reutilizables/paginacion/Paginacion.jsx'
import * as domiciliosService from '../servicios/domiciliosService'
import { buscarPorSubCategoria, buscarTodasOptimizado } from '../servicios/publicacionesService'
import { buscarTodas } from '../servicios/tiendasOficialesService'
import Banners from './Banners'
import ComponenteError from './ComponenteError'
import Publicacion from './Publicacion'
import styles from './Publicaciones.module.css'

const ITEMS_PAGINA = 15

const Publicaciones = ({ ofertas }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const topRef = useRef(null)

  const { getCarrito } = useContext(CarritoContext)
  const { idVendedorCarrito, razonSocial } = location.state || {}
  const idComprador = parseInt(localStorage.getItem('idUsuario'))
  const { idSubCategoria, idVendedor } = useParams()
  const idVendedorFormateado = Number(idVendedor)

  // Access the path from router state
  const tiendaPath = location.state?.path
  const [vistaCuadricula, setVistaCuadricula] = useState(true)
  const esOferta = ofertas === 'S'
  const [paginaActual, setPaginaActual] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(ITEMS_PAGINA)
  const [vendedor, setVendedor] = useState({
    id: idVendedorCarrito || 0,
    label: razonSocial || ''
  })
  const [state, setState] = useState({
    publicaciones: [],
    tiendas: [],
    ofertas: [],
    buscar: '',
    idVendedor: 0,
    idCategoria: 0,
    sinResultados: false
  })
  const [publicacionesMostradas, setPublicacionesMostradas] = useState([])
  const [provinciaSeleccionada, setProvinciaSeleccionada] = useState('')
  const [localidadSeleccionada, setLocalidadSeleccionada] = useState('')
  const [provincias, setProvincias] = useState([])
  const [localidades, setLocalidades] = useState([])
  const { busca, publicacionesFiltradas, tipoVendedor, estado, ordenamiento } = useSelector(
    (state) => state.filters
  )

  const showItems = useCallback(async () => {
    let publicaciones = []
    const vendedorIdValido = Number.isFinite(idVendedorFormateado)
      ? idVendedorFormateado
      : Number.isFinite(vendedor.id)
        ? vendedor.id
        : 0

    let requiereFiltroEstado = true

    if (vendedorIdValido) {
      const res = await buscarTodasOptimizado(idComprador, vendedorIdValido, 0, busca || '')
      publicaciones = res.data

      // Tiendas - moved inside this block to avoid dependency issues
      const { data: tiendas } = await buscarTodas(busca || '')
      const tienda = tiendas.filter((t) => t.idUsuario === vendedorIdValido)
      setState((prev) => ({ ...prev, tiendas: tienda }))
    } else if (idSubCategoria && idSubCategoria !== '') {
      const res = await buscarPorSubCategoria(idSubCategoria, busca || '')
      publicaciones = res.data
      requiereFiltroEstado = false
      const categoria = [...new Set(publicaciones.map((p) => p.idCategoria))]
      setState((prev) => ({ ...prev, idSubCategoria: categoria }))
    } else {
      const res = await buscarTodasOptimizado(idComprador, 0, 0, busca || '')
      publicaciones = res.data
    }

    const fechaActual = new Date()
    publicaciones = publicaciones.filter((p) => {
      if (requiereFiltroEstado && p.idPublicacionEstado !== ESTADO_PUBLICADA) {
        return false
      }

      if (p.desde && p.hasta) {
        const fechaDesde = new Date(p.desde)
        const fechaHasta = new Date(p.hasta)
        return fechaActual >= fechaDesde && fechaActual <= fechaHasta
      }

      return true
    })

    if (esOferta) {
      publicaciones = publicaciones.filter((p) => {
        if (p.precio > 0) {
          const descuento = Number(p.porcDcto) || 0
          return descuento > 0
        }
      })
    }

    if (!idSubCategoria) {
      const deepestCategory = getDeepestCommonCategoryId(publicaciones)
      if (deepestCategory) {
        dispatch(setCategoriaPath(deepestCategory))
      }
    }

    setState((prev) => ({
      ...prev,
      publicaciones,
      sinResultados: publicaciones.length === 0,
      buscar: busca
    }))
  }, [ofertas, vendedor, idSubCategoria, busca, idComprador, location.pathname])

  useEffect(() => {
    const cargarDomiciliosPublicaciones = async () => {
      if (!state.publicaciones || state.publicaciones.length === 0) {
        setProvincias([])
        setLocalidades([])
        return
      }

      if (state.publicaciones[0]?.domiciliosVendedor !== undefined) {
        return
      }

      try {
        const idsVendedoresUnicos = [...new Set(state.publicaciones.map((p) => p.idVendedor))]

        const domiciliosPorVendedor = {}
        const provinciasSet = new Set()
        const localidadesSet = new Set()

        for (const idVend of idsVendedoresUnicos) {
          try {
            const { data: domicilios } = await domiciliosService.buscarTodas(idVend, 'B')
            if (domicilios && domicilios.length > 0) {
              const domicilioPrincipal = domicilios.find(
                (d) => d.domicilioPrincipal === 1 || d.domicilioPrincipal === true
              )

              const domiciliosValidos = domicilios.filter(
                (d) =>
                  d.provincia && d.provincia.trim() && (d.localidad?.trim() || d.ciudad?.trim())
              )

              const domicilioAUsar =
                domicilioPrincipal &&
                domicilioPrincipal.provincia?.trim() &&
                (domicilioPrincipal.localidad?.trim() || domicilioPrincipal.ciudad?.trim())
                  ? domicilioPrincipal
                  : domiciliosValidos[0]

              if (domicilioAUsar) {
                domiciliosPorVendedor[idVend] = [domicilioAUsar]

                const provinciaNormalizada = domicilioAUsar.provincia
                  ?.toString()
                  .trim()
                  .toLowerCase()
                if (provinciaNormalizada) {
                  provinciasSet.add(provinciaNormalizada)
                }
                const localidad = domicilioAUsar.localidad || domicilioAUsar.ciudad
                const localidadNormalizada = localidad?.toString().trim().toLowerCase()
                if (localidadNormalizada) {
                  localidadesSet.add(localidadNormalizada)
                }
              }
            }
          } catch (error) {
            const mensaje = `Error al obtener domicilios del vendedor ${idVend}.`
            toastErrorLog(error, mensaje, 'publicaciones')
          }
        }

        const publicacionesConDomicilios = state.publicaciones.map((pub) => {
          const domicilios = domiciliosPorVendedor[pub.idVendedor] || []
          const primerDomicilioValido = domicilios[0] || null
          const localidadDelPrimer =
            (primerDomicilioValido?.localidad || primerDomicilioValido?.ciudad)?.toLowerCase() || ''

          return {
            ...pub,
            domiciliosVendedor: domicilios,
            domicilioVendedor: primerDomicilioValido,
            provinciaVendedor: primerDomicilioValido?.provincia?.toLowerCase() || '',
            localidadVendedor: localidadDelPrimer
          }
        })

        setState((prev) => ({
          ...prev,
          publicaciones: publicacionesConDomicilios
        }))

        setProvincias(Array.from(provinciasSet).sort())
        setLocalidades(Array.from(localidadesSet).sort())
      } catch (error) {
        const mensaje = 'Error al cargar domicilios de publicaciones.'
        toastErrorLog(error, mensaje, 'publicaciones')
      }
    }

    cargarDomiciliosPublicaciones()
  }, [state.publicaciones])

  const localidadesFiltradasPorProvincia = provinciaSeleccionada
    ? [
        ...new Set(
          state.publicaciones.flatMap((p) => {
            const domicilios = p.domiciliosVendedor || []
            return domicilios
              .filter((d) => d.provincia?.toString().trim().toLowerCase() === provinciaSeleccionada)
              .map((d) => (d.localidad || d.ciudad)?.toString().trim().toLowerCase())
              .filter((loc) => loc)
          })
        )
      ].sort()
    : localidades

  const onToggleClick = (event, publicacion, idPublicacion) => {
    navigate(`/Publicacion/${idPublicacion}`)
  }

  // Cargar publicaciones al montar o cambiar filtros
  useEffect(() => {
    showItems()
  }, [showItems])

  useEffect(() => {
    getCarrito()
    dispatch(setOrdenamiento({ orden: 'RELEVANTE', campo: 'idPublicacion' }))
  }, [])

  useEffect(() => {
    if (idSubCategoria) {
      dispatch(setPublicacionesFiltradas([]))
    }
  }, [idSubCategoria, dispatch])

  useEffect(() => {
    const publicacionesAMostrar = publicacionesFiltradas

    setState((prev) => ({
      ...prev,
      sinResultados: publicacionesAMostrar.length === 0
    }))

    setPaginaActual(1)
  }, [publicacionesFiltradas, ofertas, state.publicaciones])

  useEffect(() => {
    const publicacionesAMostrar = publicacionesFiltradas

    if (publicacionesAMostrar && publicacionesAMostrar.length > 0) {
      const publicacionesOrdenadas = aplicarOrdenamiento(publicacionesAMostrar, {
        orden: ordenamiento?.orden,
        campo: ordenamiento?.campo
      })
      const indiceUltimoItem = paginaActual * itemsPorPagina
      const indicePrimerItem = indiceUltimoItem - itemsPorPagina
      setPublicacionesMostradas(publicacionesOrdenadas.slice(indicePrimerItem, indiceUltimoItem))
    } else {
      setPublicacionesMostradas([])
    }
  }, [
    publicacionesFiltradas,
    ordenamiento,
    paginaActual,
    itemsPorPagina,
    ofertas,
    state.publicaciones
  ])

  const banner = state.tiendas.filter((t) => t.esBanner === true)
  const arrBannersId = [...new Set(banner.map((t) => t.idTiendaOficial && t.path))]
  const nombreTienda = state.tiendas.map((t) => t.nombreTienda)
  const nombre = [...new Set(nombreTienda.map((n) => n))].toString()

  const filtrosFijos = [
    {
      titulo: 'Vendedor',
      value: tipoVendedor,
      opciones: [
        { label: 'Empresa', valor: 4 },
        { label: 'Particular', valor: 3 }
      ],
      onChange: (v) => dispatch(setTipoVendedor(v))
    },
    {
      titulo: 'Estado',
      value: estado,
      opciones: [
        { label: 'Nuevo', valor: 'nuevo' },
        { label: 'Usado', valor: 'usado' }
      ],
      onChange: (v) => dispatch(setEstado(v))
    },
    {
      titulo: 'Provincia',
      value: provinciaSeleccionada,
      opciones: provincias.map((prov) => ({
        label: prov.charAt(0).toUpperCase() + prov.slice(1),
        valor: prov
      })),
      onChange: (v) => {
        setProvinciaSeleccionada(v)
        setLocalidadSeleccionada('')
      }
    },
    {
      titulo: 'Localidad',
      value: localidadSeleccionada,
      opciones: localidadesFiltradasPorProvincia.map((loc) => ({
        label: loc.charAt(0).toUpperCase() + loc.slice(1),
        valor: loc
      })),
      onChange: (v) => setLocalidadSeleccionada(v)
    }
  ]

  const publicacionesAMostrar = publicacionesFiltradas
  const totalPaginas = Math.max(1, Math.ceil(publicacionesAMostrar.length / itemsPorPagina))

  return (
    <div className={styles.layout} ref={topRef}>
      <aside className={styles.filtros}>
        <Filtros
          publicaciones={state.publicaciones}
          filtrosFijos={filtrosFijos}
          setVendedor={setVendedor}
          vendedor={vendedor}
          provinciaSeleccionada={provinciaSeleccionada}
          localidadSeleccionada={localidadSeleccionada}
          setProvinciaSeleccionada={setProvinciaSeleccionada}
          setLocalidadSeleccionada={setLocalidadSeleccionada}
          mostrarLimpiarFiltros={
            tipoVendedor || estado || provinciaSeleccionada || localidadSeleccionada
          }
          limpiarFiltros={idSubCategoria ? () => navigate('/Publicaciones') : undefined}
          noLimpiaFiltrosAlEntrar={!!location.state?.desdeCategoria || !!idVendedorCarrito}
        />
      </aside>
      <div className="col">
        {idVendedor && <Banners src={arrBannersId} logoPath={tiendaPath} nombreTienda={nombre} />}
        <fieldset className={`fieldset ${styles.fieldset}`}>
          <div className={styles.headerPublicaciones}>
            <legend>Publicaciones</legend>
            <div className={styles.accionesVista}>
              <OrdenarPor
                options={[
                  {
                    label: 'Más relevantes',
                    value: { orden: 'RELEVANTE', campo: 'idPublicacion' }
                  },
                  { label: 'Menor precio', value: { orden: 'ASC', campo: 'precio' } },
                  { label: 'Mayor precio', value: { orden: 'DESC', campo: 'precio' } }
                ]}
              />
              <VistaCuadricula
                requiereCuadricula={true}
                vistaCuadricula={vistaCuadricula}
                setVistaCuadricula={setVistaCuadricula}
              />
            </div>
          </div>
          {!state.sinResultados ? (
            <>
              <div className={vistaCuadricula ? styles.gridContainer : ''}>
                {publicacionesMostradas.map((publicacion) => (
                  <div
                    key={publicacion.idPublicacion}
                    className={vistaCuadricula ? '' : styles.publicacion}
                  >
                    <Publicacion
                      onToggleClick={onToggleClick}
                      publicacion={publicacion}
                      vistaCuadricula={vistaCuadricula}
                    />
                  </div>
                ))}
              </div>
              <Paginacion
                totalPaginas={totalPaginas}
                paginaActual={paginaActual}
                onCambioPagina={(num) => {
                  setPaginaActual(num)
                  topRef.current?.scrollIntoView({ behavior: 'smooth' })
                }}
                itemsPorPagina={itemsPorPagina}
                onCambioItemsPorPagina={(num) => {
                  setItemsPorPagina(num)
                  setPaginaActual(1)
                  topRef.current?.scrollIntoView({ behavior: 'smooth' })
                }}
              />
            </>
          ) : (
            <div className="row">
              <ComponenteError tipo="I" idAviso={7} />
            </div>
          )}
        </fieldset>
      </div>
    </div>
  )
}

Publicaciones.propTypes = {
  ofertas: PropTypes.string
}

export default Publicaciones
